import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import { Settings, Plus, Trash2 } from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();
  const { lang, setLanguage } = useLang();
  const [categories, setCategories] = useState([]);
  const [newCatName, setNewCatName] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [deleteCatTarget, setDeleteCatTarget] = useState(null); // category name being confirmed
  const [deletingCat, setDeletingCat] = useState(false);

  const isAdmin = user?.role === 'direction' || user?.role === 'support';

  useEffect(() => {
    if (isAdmin) {
      api.get('/categories').then(res => setCategories(res.data)).catch(() => {});
    }
  }, [isAdmin]);

  const handleLangChange = async (newLang) => {
    setLanguage(newLang);
    try { await api.put('/users/language', { language_pref: newLang }); } catch {}
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    setAddingCat(true);
    try {
      await api.post('/categories', { name: newCatName.trim() });
      setNewCatName('');
      const { data } = await api.get('/categories');
      setCategories(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally { setAddingCat(false); }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCatTarget) return;
    setDeletingCat(true);
    try {
      await api.delete('/categories/' + encodeURIComponent(deleteCatTarget));
      const { data } = await api.get('/categories');
      setCategories(data);
      setDeleteCatTarget(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally { setDeletingCat(false); }
  };

  return (
    <Layout>
      <div className="max-w-2xl animate-fade-in">
        <div className="flex items-center gap-3 mb-5 sm:mb-6">
          <Settings size={22} style={{ color: '#F77F00', flexShrink: 0 }} />
          <h1 data-testid="settings-title" className="text-xl sm:text-2xl font-bold text-white truncate">{t('settings', lang)}</h1>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {/* Profile Info */}
          <div className="apollo-card p-4 sm:p-6">
            <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Profile</h3>
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg" style={{ background: '#0F0F1A' }}>
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(247,127,0,0.15)' }}>
                <span style={{ color: '#F77F00', fontWeight: 700, fontSize: 20 }}>{user?.name?.charAt(0)}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white text-base sm:text-lg truncate">{user?.name}</p>
                <p className="text-xs sm:text-sm truncate" style={{ color: '#6B7280' }}>{user?.email}</p>
                <span className="text-xs px-2.5 py-0.5 mt-1 inline-block rounded-full font-medium" style={{ background: 'rgba(247,127,0,0.15)', color: '#F77F00' }}>
                  {t(user?.role, lang)}
                </span>
              </div>
            </div>
          </div>

          {/* Language */}
          <div className="apollo-card p-4 sm:p-6">
            <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: '#9CA3AF' }}>
              {lang === 'fr' ? 'Langue' : 'Language'}
            </h3>
            <div className="flex gap-3">
              <button data-testid="settings-lang-fr" onClick={() => handleLangChange('fr')}
                className="flex-1 p-4 rounded-lg text-center transition-all"
                style={{ background: lang === 'fr' ? 'rgba(247,127,0,0.1)' : '#0F0F1A', border: '2px solid ' + (lang === 'fr' ? '#F77F00' : 'rgba(255,255,255,0.08)'), color: lang === 'fr' ? '#F77F00' : '#9CA3AF', minHeight: '44px' }}>
                <span className="text-2xl block mb-1">FR</span><span className="text-xs">Français</span>
              </button>
              <button data-testid="settings-lang-en" onClick={() => handleLangChange('en')}
                className="flex-1 p-4 rounded-lg text-center transition-all"
                style={{ background: lang === 'en' ? 'rgba(247,127,0,0.1)' : '#0F0F1A', border: '2px solid ' + (lang === 'en' ? '#F77F00' : 'rgba(255,255,255,0.08)'), color: lang === 'en' ? '#F77F00' : '#9CA3AF', minHeight: '44px' }}>
                <span className="text-2xl block mb-1">EN</span><span className="text-xs">English</span>
              </button>
            </div>
          </div>

          {/* Category Management (Direction only) */}
          {isAdmin && (
            <div className="apollo-card p-4 sm:p-6">
              <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: '#9CA3AF' }}>
                {lang === 'fr' ? 'Gestion des catégories' : 'Category Management'}
              </h3>
              <div className="space-y-2 mb-4">
                {categories.map(cat => (
                  <div key={cat.name} className="flex items-center justify-between gap-2 p-3 rounded-lg" style={{ background: '#0F0F1A' }}>
                    <span className="text-sm text-white truncate min-w-0 flex-1">{cat.name}</span>
                    <button data-testid={"delete-cat-" + cat.name} onClick={() => setDeleteCatTarget(cat.name)} className="rounded-lg hover:bg-white/5 flex items-center justify-center flex-shrink-0" style={{ color: '#EF4444', width: 44, height: 44 }} aria-label="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  data-testid="new-category-input"
                  value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
                  className="apollo-input flex-1"
                  placeholder={lang === 'fr' ? 'Nouvelle catégorie...' : 'New category...'}
                  style={{ minHeight: '44px' }}
                />
                <button data-testid="add-category-button" onClick={handleAddCategory} disabled={addingCat} className="apollo-btn-primary flex items-center justify-center gap-2 px-4" style={{ minHeight: '44px' }}>
                  <Plus size={16} /> {lang === 'fr' ? 'Ajouter' : 'Add'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation de suppression de catégorie (remplace window.confirm pour cohérence UX ConfirmDialog) */}
      <ConfirmDialog
        open={!!deleteCatTarget}
        onCancel={() => setDeleteCatTarget(null)}
        onConfirm={handleDeleteCategory}
        title={lang === 'fr' ? 'Supprimer cette catégorie ?' : 'Delete this category?'}
        message={deleteCatTarget ? (lang === 'fr'
          ? `La catégorie « ${deleteCatTarget} » sera définitivement supprimée. Les tickets existants rattachés à cette catégorie resteront visibles mais garderont l'ancien libellé. Cette action est irréversible.`
          : `The category "${deleteCatTarget}" will be permanently deleted. Existing tickets with this category will remain visible but keep the old label. This action cannot be undone.`) : ''}
        confirmLabel={lang === 'fr' ? 'Supprimer définitivement' : 'Delete permanently'}
        cancelLabel={lang === 'fr' ? 'Annuler' : 'Cancel'}
        destructive
        submitting={deletingCat}
      />
    </Layout>
  );
}
